import { pick } from 'ramda'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { listTablePrefs } from 'app/constants'
import type InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import { DateAndTime } from 'core/components/listTable/cells/DateCell'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import type { BadgeVariant } from 'core/elements/badge/Badge'
import { createGridStatusCell, type StatusCellModel } from 'core/elements/grid/cells/GridStatusCell'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import getGridDialogButton from 'core/elements/grid/helpers/getGridDialogButton'
import type { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import Text from 'core/elements/Text'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isReadonlyUser } from 'core/utils/helpers'
import AddNodeGroupsModal from 'k8s/components/capacity-and-health/AddNodeGroupsModal'
import { isNilOrEmpty } from 'utils/fp'
import EditClusterNodeGroupsModal from '../clusters-dashboard/EditClusterNodeGroupsModal'
import EditGpuConfigurations from '../clusters-dashboard/EditGpuCofigurations'
import { listKaapiClusters } from '../kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from '../kaapi/clusters/selectors'
import { listKaapiMachineDeployments } from '../kaapi/machine-deployment/actions'
import type { IMachineDeploymentSelector } from '../kaapi/machine-deployment/model'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'
import { listKaapiMachines } from '../kaapi/machines/actions'
import MachineErrorModal from '../kaapi/machines/MachineErrorModal'
import {
  CONTROL_PLANE_UNREACHABLE_REASON,
  hasControlPlaneConnectionIssue,
  isUnhealthy,
  MachineHealthState,
  machineHealthSeverityRank,
} from '../kaapi/machines/machineHealth'
import { kaapiMachinesForClusterSelector } from '../kaapi/machines/selectors'
import { listByoHosts } from '../nodes/byoh/actions'
import DeleteNodeGroupsDialog from './DeleteNodeGroupsDialog'

type ActionParams = InferActionParams<typeof listKaapiMachines>
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  orderBy: 'metadata.creationTimestamp',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook('Machine Deployments', listTablePrefs)

export type NodeGroupSelector = IMachineDeploymentSelector

const Phase = {
  Provisioning: 'Provisioning',
  Provisioned: 'Provisioned',
  Running: 'Running',
  Failed: 'Failed',
  ScalingUp: 'ScalingUp',
  ScalingDown: 'ScalingDown',
  Deleting: 'Deleting',
  Unknown: 'Unknown',
}

const getPhaseStatus = (phase): StatusCellModel => {
  switch (phase) {
    case Phase.Running:
    case Phase.Provisioned:
      return { variant: 'success', label: phase }
    case Phase.ScalingUp:
    case Phase.ScalingDown:
    case Phase.Provisioning:
      return { variant: 'warning', label: phase }
    case Phase.Failed:
    case Phase.Deleting:
      return { variant: 'error', label: phase }
    default:
      return { variant: 'unknown', label: Phase.Unknown }
  }
}
const cellFormatFn = (value) => (isNilOrEmpty(value) ? '-' : value)

export const rollupMachineHealth = (machineDeployments, machines) => {
  return machineDeployments.map((md) => {
    const children = machines.filter(
      (m) => m?.machineDeployment?.metadata?.name === md?.metadata?.name,
    )
    const unhealthy = children
      .filter((m) => isUnhealthy(m?.machineHealth))
      .sort(
        (a, b) =>
          (machineHealthSeverityRank[b?.machineHealth?.state] || 0) -
          (machineHealthSeverityRank[a?.machineHealth?.state] || 0),
      )
    return {
      ...md,
      worstMachineHealth: unhealthy[0]?.machineHealth,
      // Carried so the rolled-up error modal can offer the same "Open VM
      // Console" link the per-machine modal does.
      worstMachineInstanceId: unhealthy[0]?.instanceId,
      unhealthyMachineCount: unhealthy.length,
    }
  })
}

const PhaseStatusCell = createGridStatusCell({
  dataFn: (phase): StatusCellModel => {
    return getPhaseStatus(phase)
  },
})

// Rolled-up node group health as a badge. The reason - not the state - picks
// the wording, because a node group can be blocked by a control plane the
// management cluster cannot reach, which is not a scaling problem at all.
export const getNodeGroupStatusBadge = (health): { variant: BadgeVariant; text: string } => {
  const variant = health?.state === MachineHealthState.Stuck ? 'warning' : 'error'
  if (health?.reason === CONTROL_PLANE_UNREACHABLE_REASON) {
    return { variant, text: 'Connection Lost' }
  }
  if (health?.reason === 'NodeJoinTimeout') {
    return { variant, text: 'Not Joining' }
  }
  return { variant, text: 'Scaling blocked' }
}

function NodeGroupStatusCell({ item }: { item?: any }) {
  const [showModal, setShowModal] = useState(false)
  const health = item?.worstMachineHealth
  const { variant, text } = getNodeGroupStatusBadge(health)

  return (
    <>
      <Badge
        variant={variant}
        text={text}
        tooltipBody="Click to view error details"
        onClick={(e) => {
          e.stopPropagation()
          setShowModal(true)
        }}
      />
      {showModal && (
        <MachineErrorModal
          machineName={item?.metadata?.name}
          reason={health?.reason}
          message={`${item?.unhealthyMachineCount} node(s) affected. ${health?.message}`}
          instanceId={item?.worstMachineInstanceId}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  )
}

function NodeGroupOrPhaseStatusCell(props: any) {
  const health = props?.item?.worstMachineHealth
  const cluster = props?.item?.cluster
  const phase = props?.value

  if (health) {
    return <NodeGroupStatusCell {...props} />
  }

  if (hasControlPlaneConnectionIssue(cluster) && phase === Phase.ScalingUp) {
    return (
      <Badge
        variant="warning"
        text={phase}
        tooltipBody="Scaling is paused. The management cluster has lost API connectivity to the workload cluster's control plane. Readiness cannot be verified until the connection is restored."
      />
    )
  }

  return <PhaseStatusCell {...props} />
}

const columns = (cluster: any): GridViewColumn<any>[] => [
  {
    key: 'metadata.name',
    label: 'Name',
  },
  {
    key: 'nodeType',
    label: 'Node Type',
    formatFn: (value) => value || '-',
  },
  {
    key: 'status.phase',
    label: 'Status',
    CellComponent: NodeGroupOrPhaseStatusCell,
    memoizeCell: false,
  },
  ...(cluster?.isBYOHCluster
    ? []
    : [
        {
          key: 'openStackMachineTemplate.spec.template.spec.flavor',
          label: 'Flavor',
        },
      ]),
  {
    key: 'spec.autoscaling',
    label: 'Auto Scaling',
  },
  {
    key: 'spec.template.spec.version',
    label: 'K8s Version',
    formatFn: cellFormatFn,
  },
  {
    key: 'metadata.creationTimestamp',
    label: 'Created',
    CellComponent: DateAndTime,
  },
  {
    key: 'spec.replicas',
    label: 'Desired Replicas',
    formatFn: cellFormatFn,
  },
  {
    key: 'status.readyReplicas',
    label: 'Ready Replicas',
    formatFn: cellFormatFn,
  },
  {
    key: 'spec.autoscalingMaxNodes',
    label: 'Max Nodes',
    CellComponent: (props) => {
      return (
        <Text variant="body2">
          {props?.item?.spec?.autoscaling === 'Disabled' ? '-' : props?.value}
        </Text>
      )
    },
  },
]

export default function ListNodeGroups() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [open, setOpen] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedNodeGroup, setSelectedNodeGroup] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showEditGpuConfigModal, setShowEditGpuConfigModal] = useState(false)
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')

  // Use clusters loaded by parent (ClusterDetailTabs)
  const clusters = useSelector(kaapiClustersOverviewSelector)
  const cluster = clusters?.find(
    (cluster) => cluster.metadata.name === k8sPluginGlobalPerfs?.cluster,
  )
  // BYOH install/join failures are reported only on the ByoHost, which the
  // machine health classifier reads to tell a failed join from an unclaimed host.
  // Gated so an OpenStack-only cluster never issues the byohosts/byomachines
  // requests - the CRDs need not even be installed there.
  useListAction(listByoHosts, { preRequisites: !!cluster?.isBYOHCluster })
  const machinesSelector = kaapiMachinesForClusterSelector({
    clusterName: k8sPluginGlobalPerfs.cluster,
  })
  const machines = useSelector(machinesSelector) || []

  // Fetch Node Groups
  const { loading: loadingMachineDeployments, reload: reloadMachineDeployments } = useListAction(
    listKaapiMachineDeployments,
  )
  const machineDeploymentSelector = useMemo(
    () =>
      kaapiMachineDeploymentsForClusterSelector({
        clusterName: k8sPluginGlobalPerfs.cluster,
      }),
    [k8sPluginGlobalPerfs.cluster],
  )
  const machineDeployments = useSelector(machineDeploymentSelector)

  const machineDeploymentsWithHealth = useMemo(() => {
    return rollupMachineHealth(machineDeployments, machines).map((md) => ({
      ...md,
      cluster,
    }))
  }, [machineDeployments, machines, cluster])

  // Get the machine deployments that have gpu nodes
  const machineDeploymentGPUDetails = useMemo(() => {
    return machineDeployments.reduce((acc, md) => {
      return {
        ...acc,
        [md?.metadata?.name]:
          machines.find((machine) => {
            return (
              machine?.machineDeployment?.metadata?.name === md?.metadata?.name &&
              !!machine?.gpu.enabled
            )
          })?.gpu || {},
      }
    }, {})
  }, [machineDeployments, machines])

  // Check if this is the last running node in the cluster
  const isLastRunningNode: boolean = useMemo(
    () => machines?.length === 1 && machines[0]?.status.phase === 'Running',
    [machines],
  )

  const k8sVersion = useMemo(() => {
    if (!cluster?.hostedControlPlane?.spec?.version && !cluster?.kubeadmControlPlane?.spec?.version)
      return ''

    return cluster?.hostedControlPlane?.spec?.version || cluster?.kubeadmControlPlane?.spec?.version
  }, [cluster])

  const isClusterClass = useMemo(() => {
    return !!cluster?.spec?.topology
  }, [cluster])

  const batchActions = [
    ...(cluster?.isBYOHCluster
      ? []
      : [
          {
            icon: 'edit',
            label: 'Edit',
            handleAction: (nodegroup) => {
              if (nodegroup?.length === 0) return
              setSelectedNodeGroup(nodegroup[0])
              setShowEditModal(true)
            },
            disabled: !isClusterClass,
            disabledTooltip: 'Cluster must be upgraded to ClusterClass to manage node groups',
          },
          {
            label: 'Delete',
            icon: 'trash-alt',
            handleAction: (nodegroup) => {
              setSelectedNodeGroup(nodegroup)
              setShowDeleteDialog(true)
            },
          },
        ]),
    {
      label: 'Edit GPU Configuration',
      icon: 'edit',
      handleAction: (nodegroups) => {
        if (nodegroups?.length === 0) return
        setSelectedNodeGroup(nodegroups[0])
        setShowEditGpuConfigModal(true)
      },
      cond: (nodegroups) =>
        nodegroups?.length === 1 &&
        !!machineDeploymentGPUDetails?.[nodegroups[0]?.metadata?.name]?.enabled,
      disabledTooltip: 'Node group does not have GPU nodes',
    },
  ]

  const rowMenuItems = [
    ...(cluster?.isBYOHCluster
      ? []
      : [
          {
            label: 'Delete',
            icon: 'trash-alt',
            handleClick: (nodegroup) => {
              setSelectedNodeGroup([nodegroup])
              setShowDeleteDialog(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: true,
          },
          {
            label: 'Edit',
            icon: 'edit',
            handleClick: (nodegroup) => {
              setSelectedNodeGroup(nodegroup)
              setShowEditModal(true)
            },
            refreshAfterSuccess: true,
            hideIfDisabled: true,
            disabled: !isClusterClass,
          },
        ]),
    {
      label: 'Edit GPU Configuration',
      icon: 'edit',
      handleClick: (nodegroup) => {
        setSelectedNodeGroup(nodegroup)
        setShowEditGpuConfigModal(true)
      },
      refreshAfterSuccess: true,
      hideIfDisabled: true,
      cond: (nodegroup) => !!machineDeploymentGPUDetails?.[nodegroup?.metadata?.name]?.enabled,
    },
  ]

  const showAddNodeGroupButton = useMemo(
    () => !isReadonlyUser() && !cluster?.isBYOHCluster,
    [cluster, isReadonlyUser],
  )

  const tableColumns = useMemo(() => columns(cluster), [cluster])

  return (
    <>
      <DocumentMeta title="Capi Node Groups Overview" data-testid="node-groups-overview" />
      {open && (
        <AddNodeGroupsModal
          onClose={() => setOpen(false)}
          k8sVersion={k8sVersion}
          data-testid="add-node-group-modal"
        />
      )}
      {showEditModal && (
        <EditClusterNodeGroupsModal
          onClose={() => setShowEditModal(false)}
          rows={[selectedNodeGroup]}
          isClusterClass={isClusterClass}
          selectedNodeIndex={
            cluster?.spec?.topology?.workers?.machineDeployments?.findIndex(
              (md) =>
                md?.name ===
                selectedNodeGroup?.metadata?.labels?.['topology.cluster.x-k8s.io/deployment-name'],
            ) || 0
          }
          data-testid="edit-node-group-modal"
          clusterName={cluster?.metadata?.name}
        />
      )}
      {showEditGpuConfigModal && (
        <EditGpuConfigurations
          onClose={() => setShowEditGpuConfigModal(false)}
          rows={[selectedNodeGroup]}
          data-testid="edit-gpu-configurations-modal"
          clusterName={cluster?.metadata?.name}
          gpuDetails={machineDeploymentGPUDetails[selectedNodeGroup?.metadata?.name]}
        />
      )}
      {showDeleteDialog && (
        <DeleteNodeGroupsDialog
          rows={selectedNodeGroup}
          onClose={() => setShowDeleteDialog(false)}
          isLastRunningNode={isLastRunningNode}
          reloadMachineDeployments={reloadMachineDeployments}
          cluster={cluster}
          data-testid="delete-node-group-dialog"
        />
      )}
      <ListContainer<any, any>
        searchTargets={['metadata.name', 'status.phase']}
        uniqueIdentifier="uid"
        loading={loadingMachineDeployments}
        loadingMessage="Loading..."
        onRefresh={reloadMachineDeployments}
        data={machineDeploymentsWithHealth}
        columns={tableColumns}
        {...pick(listTablePrefs, params)}
        getParamsUpdater={getParamsUpdater}
        label="Node Groups"
        showItemsCountInLabel
        showBreadcrumbs={false}
        batchActions={batchActions}
        extraToolbarContent={
          showAddNodeGroupButton && (
            <CreateButton
              icon="add"
              onClick={() => setOpen(true)}
              data-testid="add-node-group-button"
              disabled={!isClusterClass}
              tooltip={
                !isClusterClass
                  ? 'Cluster must be upgraded to ClusterClass to manage node groups'
                  : undefined
              }
            >
              Add Node Group
            </CreateButton>
          )
        }
        rowMenuItems={rowMenuItems}
        showRowMenuForSingleRowActions
        data-testid="node-groups-list-container"
      />
    </>
  )
}
